import React, { useId, useMemo } from "react";
import type { ReactDatePickerCustomHeaderProps } from "react-datepicker";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "../button/button.tsx";
import { Select, type SelectOption } from "../select/select.tsx";

import styles from "./date-picker.module.css";

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const YEAR_WINDOW_BACK = 120;

export type DatePickerCalendarHeaderExtraProps = {
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  yearOrder?: "asc" | "desc";
};

export type DatePickerCalendarHeaderProps = ReactDatePickerCustomHeaderProps &
  DatePickerCalendarHeaderExtraProps;

/** True if at least one calendar day in (year, month) lies within [minDate, maxDate] (inclusive). */
function monthHasSelectableDay(
  year: number,
  month: number,
  minDate: Date | undefined,
  maxDate?: Date,
): boolean {
  const monthStart = new Date(year, month, 1);
  monthStart.setHours(0, 0, 0, 0);
  const monthEnd = new Date(year, month + 1, 0);
  monthEnd.setHours(0, 0, 0, 0);

  if (maxDate != null) {
    const max = new Date(maxDate);
    max.setHours(0, 0, 0, 0);

    if (monthStart > max) return false;
  }

  if (minDate != null) {
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0);
    if (monthEnd < min) return false;
  }

  return true;
}

export function DatePickerCalendarHeader({
  monthDate,
  changeMonth,
  changeYear,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  customHeaderCount,
  minDate,
  maxDate,
  disabled = false,
  yearOrder = "desc",
}: DatePickerCalendarHeaderProps) {
  const uid = useId().replace(/:/g, "");
  const suffix = customHeaderCount > 0 ? `-${customHeaderCount}` : "";
  const viewDate = monthDate ?? new Date();

  const minYear = useMemo(() => {
    if (minDate != null) return minDate.getFullYear();
    const upper = maxDate?.getFullYear() ?? new Date().getFullYear();
    return upper - YEAR_WINDOW_BACK;
  }, [minDate, maxDate]);
  const maxYear = maxDate?.getFullYear() ?? new Date().getFullYear();

  const yearOptions: SelectOption[] = useMemo(() => {
    const opts: SelectOption[] = [];
    if (yearOrder === "asc") {
      for (let y = minYear; y <= maxYear; y += 1) {
        opts.push({ value: String(y), label: String(y) });
      }
    } else {
      /** Newest first so DOB / max-bound pickers open near the current year without long scroll. */
      for (let y = maxYear; y >= minYear; y -= 1) {
        opts.push({ value: String(y), label: String(y) });
      }
    }
    return opts;
  }, [minYear, maxYear, yearOrder]);

  const monthOptions: SelectOption[] = useMemo(() => {
    const y = viewDate.getFullYear();
    const opts: SelectOption[] = [];
    for (let m = 0; m <= 11; m += 1) {
      opts.push({
        value: String(m),
        label: MONTH_LABELS[m],
        disabled: !monthHasSelectableDay(y, m, minDate, maxDate),
      });
    }
    return opts;
  }, [viewDate, minDate, maxDate]);

  const y = viewDate.getFullYear();
  const m = viewDate.getMonth();

  const yearValue =
    y >= minYear && y <= maxYear
      ? String(y)
      : String(Math.min(Math.max(y, minYear), maxYear));

  const monthValue = useMemo(() => {
    const cur = String(m);
    const curOpt = monthOptions.find((o) => o.value === cur);
    if (curOpt && !curOpt.disabled) return cur;
    const first = monthOptions.find((o) => !o.disabled);
    return first?.value ?? cur;
  }, [m, monthOptions]);

  return (
    <div className={styles.calendarHeader}>
      <Button
        type="button"
        variant="muted-outline"
        size="sm"
        className={styles.calendarHeaderNav}
        aria-label="Previous month"
        disabled={disabled || prevMonthButtonDisabled}
        onClick={decreaseMonth}
        startIcon={<ChevronLeft size={16} strokeWidth={2} aria-hidden />}
      />
      <div className={styles.calendarHeaderSelect}>
        <Select
          id={`dp-cal-m-${uid}${suffix}`}
          value={monthValue}
          disabled={disabled}
          density="compact"
          placeholder="Month"
          options={monthOptions}
          onChange={(v) => changeMonth(Number.parseInt(v, 10))}
        />
      </div>
      <div className={styles.calendarHeaderSelectYear}>
        <Select
          id={`dp-cal-y-${uid}${suffix}`}
          value={yearValue}
          disabled={disabled}
          density="compact"
          placeholder="Year"
          options={yearOptions}
          onChange={(v) => changeYear(Number.parseInt(v, 10))}
          panelMinWidth={100}
        />
      </div>
      <Button
        type="button"
        variant="muted-outline"
        size="sm"
        className={styles.calendarHeaderNav}
        aria-label="Next month"
        disabled={disabled || nextMonthButtonDisabled}
        onClick={increaseMonth}
        startIcon={<ChevronRight size={16} strokeWidth={2} aria-hidden />}
      />
    </div>
  );
}
